import { RefObject, useRef } from 'react'

import {
  AuctionResponse,
  AuctionResponseDepartment,
  AuctionViewing,
} from '@bonhams1793/bonhams-typescript'

import { LotResponse } from '@nx/api'
import { FireAuctionViewings } from '@nx/fire/auction-viewings'
import { FireLink } from '@nx/fire/link'
import { FireSymbolsTable } from '@nx/fire/lot-number-and-symbols'
import { Heading } from '@nx/fire/typography'
import { buildDepartmentPath, httpToHttps } from '@nx/helpers'
import { SmartlingAttributes } from '@nx/smartling'
import { getTranslate } from '@nx/translations'

import { StyledAccordionLotSymbols } from './FireAdditionalInformation.styles'
import translate from './FireAdditionalInformation.translate.json'

export enum InfoType {
  auction = 'auctionInfo',
  symbol = 'symbols',
  relatedDepartment = 'relatedDepartments',
  auctionViewing = 'auctionViewings',
  artist = 'artists',
  conditionOfSale = 'conditionsOfSale',
}

const { text } = getTranslate(translate)

export const useInformation = ({
  auction,
  lot,
  auctionViewings,
  domain,
}: UseInformationProps): UseInformationReturnType => {
  const auctionInfoRef = useRef<HTMLButtonElement>(null)
  const lotSymbolsRef = useRef<HTMLButtonElement>(null)
  const relatedDepartmentsRef = useRef<HTMLButtonElement>(null)
  const auctionViewingsRef = useRef<HTMLButtonElement>(null)
  const artistsRef = useRef<HTMLButtonElement>(null)
  const conditionsOfSaleRef = useRef<HTMLButtonElement>(null)

  const getAuctionInfo = () => {
    const {
      snippets: { body, all_special_notices },
      lExhibition,
    } = auction

    return all_special_notices?.length
      ? {
          id: InfoType.auction,
          ref: auctionInfoRef,
          title: lExhibition ? text('exhibitionInfo') : text('auctionInfo'),
          children: (
            <>
              {body && (
                <div
                  {...SmartlingAttributes.noTranslate}
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              )}
              {all_special_notices.map(({ notice_type, text }, index) => (
                <div key={index} {...SmartlingAttributes.noTranslate}>
                  <Heading
                    $level={5}
                    as="h4"
                    css={`
                      margin-bottom: 10px;
                    `}
                  >
                    {notice_type}
                  </Heading>
                  <div dangerouslySetInnerHTML={{ __html: text }} />
                </div>
              ))}
            </>
          ),
        }
      : null
  }

  const getLotSymbols = () => {
    const entity = lot || auction

    return entity && entity.lot_symbols?.length > 0
      ? {
          id: InfoType.symbol,
          ref: lotSymbolsRef,
          title: text('lotSymbols'),
          children: (
            <StyledAccordionLotSymbols {...SmartlingAttributes.noTranslate}>
              <FireSymbolsTable
                symbols={entity.lot_symbols.map((s) => ({
                  symbol: s.sLotSymbol,
                  longDescription: s.sLongDesc,
                  shortDescription: s.sShortDesc,
                }))}
              />
            </StyledAccordionLotSymbols>
          ),
        }
      : null
  }

  const getRelatedDepartments = () =>
    auction.departments?.length > 0
      ? {
          id: InfoType.relatedDepartment,
          ref: relatedDepartmentsRef,
          title: text('relatedDepartments'),
          children: (
            <ul>
              {auction.departments.map(
                (department: AuctionResponseDepartment, index: number) => (
                  <li key={index}>
                    <FireLink
                      href={buildDepartmentPath(
                        department.sDepartment,
                        department.sDepartmentName,
                        domain
                      )}
                    >
                      {department.sDepartmentName}
                    </FireLink>
                  </li>
                )
              )}
            </ul>
          ),
        }
      : null

  const getAuctionViewings = () =>
    auction.sBidOnlineStatus !== 'EN' &&
    auctionViewings?.filter((auctionViewing) => auctionViewing.location?.code)
      .length
      ? {
          id: InfoType.auctionViewing,
          ref: auctionViewingsRef,
          title: auction?.lExhibition
            ? text('exhibitionViewings')
            : text('auctionViewings'),
          children: (
            <FireAuctionViewings
              auctionViewings={auctionViewings}
              groupBy="location"
              groupNameComponent="link"
              domain={domain}
            />
          ),
        }
      : null

  const getArtists = () =>
    auction.artists && auction.artists.length > 0
      ? {
          id: InfoType.artist,
          ref: artistsRef,
          title: text('relatedArtists'),
          children: (
            <ul {...SmartlingAttributes.noTranslate}>
              {auction.artists &&
                auction.artists.map((artist) => (
                  <li key={artist.iArtistNo}>
                    <FireLink
                      href={`${domain || ''}/artists/${artist.iArtistNo}`}
                      forceAnchor={true}
                    >
                      {artist.sArtistTitle}
                    </FireLink>
                  </li>
                ))}
            </ul>
          ),
        }
      : null

  const getConditionsOfSale = () => {
    const conditionSaleUrl = auction?.conditions_of_sale?.image_url
      ? auction.conditions_of_sale.image_url
      : lot?.sale_info?.conditions_of_sale?.image_url

    return {
      id: InfoType.conditionOfSale,
      ref: conditionsOfSaleRef,
      title: text('conditionsOfSale'),
      children: (
        <FireLink
          href={
            conditionSaleUrl
              ? httpToHttps(conditionSaleUrl)
              : `${domain || ''}/legals/`
          }
          target="_blank"
          rel="noreferrer"
          forceAnchor={true}
        >
          {text('viewConditionsOfSale')}
        </FireLink>
      ),
    }
  }

  return [
    getAuctionInfo(),
    getLotSymbols(),
    getRelatedDepartments(),
    getAuctionViewings(),
    getArtists(),
    getConditionsOfSale(),
  ].filter(Boolean)
}

interface UseInformationProps {
  auction: AuctionResponse
  lot?: LotResponse
  auctionViewings?: AuctionViewing[]
  domain?: string
}

type UseInformationReturnType = {
  id: string
  title: string
  ref: RefObject<HTMLButtonElement>
  children: JSX.Element
}[]
